import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { createPortal } from "react-dom";
import NatureFavoritesPicker from "./NatureFavoritesPicker.jsx";
import {
  loadPreferences,
  saveFavoriteNatureSoundIds,
} from "../../services/ai/memory/aiMemory.js";
import "./AuriaNatureOnboarding.css";
import TopbarInner from "../topbar/TopbarInner";
import { useBackground } from "../../hooks/useBackground";

const EDITOR_HISTORY_KEY =
  "natureFavoritesEditorOpen";

function NatureFavoritesEditor({
  uid,
  naturalSounds = [],
  open: controlledOpen,
  onOpenChange,
  onSaved,
}) {
  const navigate = useNavigate();
  const location = useLocation();
const { bgIndex, imageFiles, desktopImageFiles } = useBackground(true);
const [internalOpen, setInternalOpen] = useState(false);

const open =
  typeof controlledOpen === "boolean"
    ? controlledOpen
    : internalOpen;

const setOpen = useCallback((nextOpen) => {
  setInternalOpen(nextOpen);
  onOpenChange?.(nextOpen);
}, [onOpenChange]);
  const [savedIds, setSavedIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const overlayRef = useRef(null);

  const editorIsInHistory =
    location.state?.[EDITOR_HISTORY_KEY] === true;

  useEffect(() => {
    if (!uid) return;

    loadPreferences(uid)
      .then((prefs) => {
        const favoriteIds =
          prefs.favoriteNatureSoundIds || [];

        setSavedIds(favoriteIds);
        setSelectedIds(favoriteIds);
      })
      .catch(() => {
        setSavedIds([]);
        setSelectedIds([]);
      });
  }, [uid]);

  const selectedSounds = useMemo(() => {
    const soundsById = new Map(
      naturalSounds.map((sound) => [
        String(sound.id),
        sound,
      ]),
    );

    return savedIds
      .map((id) =>
        soundsById.get(String(id)),
      )
      .filter(Boolean)
      .slice(0, 3);
  }, [naturalSounds, savedIds]);

  /*
   * Synchronise l’éditeur avec l’historique.
   *
   * Retour navigateur :
   * éditeur ouvert → page profil.
   *
   * Retour avant du navigateur :
   * page profil → réouverture de l’éditeur.
   */
useEffect(() => {
  if (editorIsInHistory) {
    setSelectedIds(savedIds);
    setOpen(true);
    return;
  }

  setSelectedIds(savedIds);
  setOpen(false);
}, [editorIsInHistory, savedIds, setOpen]);

  const closeEditor = () => {
    setSelectedIds(savedIds);

    if (editorIsInHistory) {
      navigate(-1);
      return;
    }

    setOpen(false);
  };

  useLayoutEffect(() => {
    if (!open) return undefined;

    const html =
      document.documentElement;
    const body = document.body;

    const previousHtmlOverflow =
      html.style.overflow;
    const previousBodyOverflow =
      body.style.overflow;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    let secondFrameId = 0;

    const firstFrameId =
      window.requestAnimationFrame(() => {
        if (overlayRef.current) {
          overlayRef.current.scrollTop = 0;
          overlayRef.current.scrollLeft = 0;
        }

        secondFrameId =
          window.requestAnimationFrame(() => {
            if (overlayRef.current) {
              overlayRef.current.scrollTop = 0;
              overlayRef.current.scrollLeft = 0;
            }
          });
      });

    return () => {
      window.cancelAnimationFrame(
        firstFrameId,
      );

      window.cancelAnimationFrame(
        secondFrameId,
      );

      html.style.overflow =
        previousHtmlOverflow;

      body.style.overflow =
        previousBodyOverflow;
    };
  }, [open]);

  const save = async () => {
    if (
      !uid ||
      selectedIds.length !== 3 ||
      saving
    ) {
      return;
    }

    setSaving(true);

    try {
      const nextSavedIds = [
        ...selectedIds,
      ];

      await saveFavoriteNatureSoundIds(
        uid,
        nextSavedIds,
      );

      setSavedIds(nextSavedIds);
      onSaved?.(nextSavedIds);

      /*
       * Même sortie que le bouton Annuler :
       * on retire l’étape éditeur de l’historique
       * et on revient sur le profil.
       */
      if (editorIsInHistory) {
        navigate(-1);
      } else {
        setOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
<section className="profile-nature-favorites">
  <div className="profile-nature-favorites__copy">
    <h3>Ambiances sélectionnées</h3>

    {selectedSounds.length > 0 ? (
      <ul
        className="profile-nature-favorites__selected"
        aria-label="Vos ambiances favorites"
      >
        {selectedSounds.map((sound) => (
          <li
            key={sound.id}
            aria-label={sound.name}
            title={sound.name}
          >
            <span
              className="profile-nature-favorites__visual"
              aria-hidden="true"
            >
              {sound.image ? (
                <img
                  src={sound.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="profile-nature-favorites__empty">
        Aucune ambiance enregistrée.
      </p>
    )}
  </div>

      {open &&
      typeof document !== "undefined"
        ? createPortal(
            <div
              ref={overlayRef}
              className="auria-onboarding auria-onboarding--editor"
              role="dialog"
              aria-modal="true"
              aria-labelledby="auria-ambiences-title"
              style={{ "--editor-bg-mobile": `url("${imageFiles[bgIndex]}")`, "--editor-bg-desktop": `url("${desktopImageFiles[bgIndex]}")` }}
            >
<TopbarInner
  title="Ambiances favorites"
  onBack={() => { if (!saving) closeEditor(); }}
  showLogo={true}
  showThemeDropdown={true}
  isPremium={true}
/>
              <div className="auria-onboarding__stage auria-onboarding__stage--editor">
                <div className="auria-onboarding__selection">
                  <div className="auria-onboarding__head">
                    <p className="auria-onboarding__eyebrow">
                      Personnalisation
                    </p>

                    <h1 id="auria-ambiences-title">
                      Choisissez trois
                      ambiances
                    </h1>

                    <p>
                      Écoutez-les, puis
                      sélectionnez les trois
                      que vous préférez.
                    </p>
                  </div>

                  <NatureFavoritesPicker
                    sounds={naturalSounds}
                    selectedIds={selectedIds}
                    onChange={setSelectedIds}
                  />

<div className="profile-nature-favorites__actions">
  <button
    type="button"
    className="auria-onboarding__cta"
    onClick={save}
    disabled={selectedIds.length !== 3 || saving}
  >
    {saving ? "Enregistrement…" : "Enregistrer"}
  </button>

  <button
    type="button"
    className="profile-nature-favorites__cancel"
    onClick={closeEditor}
  >
    Annuler
  </button>
</div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
}

export default NatureFavoritesEditor;